import { useEffect, useState } from 'react';
import {
  locationsListResponseSchema,
  type TransferLocationSearchRow,
} from '../schemas/transferLocationSearch';
import {
  transferSourceInventoryListResponseSchema,
  type TransferSourceInventoryRow,
} from '../schemas/transferSourceInventory';
import { LocationService } from '../utility';
import { useDebouncedEffect } from './debounce';

export const useLocationHook = (id, warehouseId) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        locationId: id,
        warehouseId,
      };
      const data = await LocationService.getLocationSummary(id, params);
      setData(data || []);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id && warehouseId) {
      fetchData();
    }
  }, [id, warehouseId]);

  return { data, loading, error, refreshData: fetchData };
};

export const TransferLocationSearchHook = (warehouseId: string | undefined) => {
  const [data, setData] = useState<TransferLocationSearchRow[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);
  const [totalData, setTotalData] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [filter, setFilter] = useState<{
    limit: number;
    page: number;
    search: string;
    isPickingLocation?: true;
  }>({
    limit: 10,
    page: 1,
    search: '',
    isPickingLocation: true,
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const params: Record<string, unknown> = Object.fromEntries(
        Object.entries({ ...filter, warehouseId }).filter(([, v]) => v !== undefined),
      );

      const res = await LocationService.getLocations(params);
      const parsed = locationsListResponseSchema.parse(res);

      setData(parsed.data.map(loc => ({ ...loc, search: filter.search })));
      setTotalData(parsed.total);
      setTotalPages(parsed.totalPages);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  useDebouncedEffect(
    () => {
      if (!warehouseId) {
        setData([]);
        return;
      }

      fetchData();
    },
    [filter, warehouseId],
    500,
  );

  const pageChanged = (page: number) => {
    const temp = {
      ...filter,
      page,
    };
    setFilter({ ...temp });
  };

  const filterChanged = (key: string, value: unknown) => {
    const temp = {
      ...filter,
      page: 1,
      [key]: value,
    };
    setFilter({ ...temp });
  };

  return {
    data,
    error,
    loading,
    totalData,
    totalPages,
    filter,
    filterChanged,
    pageChanged,
  };
};

export const TransferOriginLocationSearchHook = (
  warehouseId: string | undefined,
  productId: string,
  suggestedTransferId: string,
) => {
  const [data, setData] = useState<TransferSourceInventoryRow[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);
  const [totalData, setTotalData] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [filter, setFilter] = useState<{
    limit: number;
    page: number;
    search: string;
    isPickingLocation: boolean;
  }>({
    limit: 10,
    page: 1,
    search: '',
    isPickingLocation: false,
  });

  const fetchData = async () => {
    try {
      setLoading(true);

      const params = {
        ...filter,
        warehouseId,
        productId,
        suggestedTransferId,
      };

      const res = await LocationService.getSourceLocations(params);
      const parsed = transferSourceInventoryListResponseSchema.parse(res);

      setData(parsed.data);
      setTotalData(parsed.total);
      setTotalPages(parsed.totalPages);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  useDebouncedEffect(
    () => {
      if (!warehouseId || !productId || !suggestedTransferId) {
        setData([]);
        return;
      }

      fetchData();
    },
    [filter, warehouseId, productId, suggestedTransferId],
    500,
  );

  const pageChanged = (page: number) => {
    const temp = {
      ...filter,
      page,
    };
    setFilter({ ...temp });
  };

  const filterChanged = (key: string, value: unknown) => {
    const temp = {
      ...filter,
      page: 1,
      [key]: value,
    };
    setFilter({ ...temp });
  };

  return {
    data,
    error,
    loading,
    totalData,
    totalPages,
    filter,
    filterChanged,
    pageChanged,
  };
};
